import type { LiveChatMessage } from "@hobbyshopify/backend-client";
import { BaseController, Rx, RxList, type SubtreeModel } from "subtree.js/core";
import type { AppDeps } from "../../../../app-deps";
import { hideChatMessage, loadChat, postChatMessage } from "../../../../core/ops/live-ops";
import {
  busyForm,
  type FormStatus,
  failedForm,
  idleForm,
  idleLoad,
  type LoadStatus,
} from "../../../../core/primitives/load-status";
import { Loader } from "../../../../core/primitives/loader";

export class CockpitChatState {
  readonly status = new Rx<LoadStatus>(idleLoad);
  readonly messages = new RxList<LiveChatMessage>();
  /** Held here, not in the panel: the cockpit mounts one panel at a time, so a draft inside it dies on every tab change. */
  readonly draft = new Rx("");
  readonly post = new Rx<FormStatus>(idleForm);
  readonly hide = new Rx<FormStatus>(idleForm);
}

export abstract class CockpitChatActions {
  abstract setDraft(value: string): void;
  abstract send(): Promise<void>;
  abstract hide(messageId: string): Promise<void>;
}

export type CockpitChatDeps = Pick<AppDeps, "client" | "authHandler">;

/** A fallback for environments with no realtime provider, and a safety net for a dropped connection mid-stream. */
export const CHAT_POLL_MS = 20_000;

/**
 * The room, in the room where the host is. The host posts through the same
 * endpoint buyers do; hiding is a soft delete. No realtime subscription of its
 * own: the cockpit holds one connection and calls `reload` on a chat event.
 */
export class CockpitChatController extends BaseController implements CockpitChatActions {
  readonly state = new CockpitChatState();
  readonly #list: Loader<string, LiveChatMessage[]>;

  constructor(
    subtree: SubtreeModel,
    readonly deps: CockpitChatDeps,
    readonly host: { streamId: string },
  ) {
    super();
    subtree.put(CockpitChatState, this.state);
    subtree.put(CockpitChatActions, this);
    this.#list = this.own(
      new Loader({
        status: this.state.status,
        fetch: (streamId: string) => loadChat({ ...deps, streamId }),
        loaded: (messages) => {
          this.state.messages.value = messages;
        },
      }),
    );
    void this.reload();
    const timer = setInterval(() => void this.reload(), CHAT_POLL_MS);
    this.autoDispose(() => clearInterval(timer));
  }

  reload = (): Promise<void> => this.#list.load(this.host.streamId);

  setDraft = (value: string) => {
    this.state.draft.value = value;
  };

  send = async () => {
    const text = this.state.draft.value.trim();
    if (text === "" || this.state.post.value.busy) return;
    this.state.post.value = busyForm;
    const r = await postChatMessage({ ...this.deps, streamId: this.host.streamId, text });
    if (this.isDisposed) return;
    if (!r.ok) {
      this.state.post.value = failedForm(r.errors);
      return;
    }
    this.state.post.value = idleForm;
    this.state.draft.value = "";
    await this.reload();
  };

  hide = async (messageId: string) => {
    if (this.state.hide.value.busy) return;
    this.state.hide.value = busyForm;
    const r = await hideChatMessage({ ...this.deps, streamId: this.host.streamId, messageId });
    if (this.isDisposed) return;
    if (!r.ok) {
      this.state.hide.value = failedForm(r.errors);
      return;
    }
    this.state.hide.value = idleForm;
    await this.reload();
  };
}
